/**
 * VoicePlayerContext — глобальный mini-player для голосовых.
 *
 * АУДИО-ЭЛЕМЕНТ ЖИВЁТ В ПРОВАЙДЕРЕ — поэтому play не останавливается когда
 * Virtuoso демонтирует VoiceMessage (далеко скроллим / выходим из чата).
 * Каждый VoiceMessage — это просто UI-обёртка: на play зовёт context.play(meta, src).
 */
import { createContext, useContext, useState, useRef, useCallback, useEffect, type ReactNode } from "react";

export interface VoicePlayerMeta {
  messageId: number;
  attachmentId: number;
  authorName: string;
  ts: string;
  durationSec: number | null;
}

interface VoicePlayerCtx {
  meta: VoicePlayerMeta | null;
  playing: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  muted: boolean;
  /** Запустить голосовое — устанавливает src в общий <audio> и play(). */
  play: (meta: VoicePlayerMeta, src: string) => void;
  /** Лёгкая регистрация playFn для prev/next (вызывается из VoiceMessage на mount). */
  registerPlayable: (messageId: number, playFn: () => void) => () => void;
  togglePlayPause: () => void;
  cyclePlaybackRate: () => void;
  toggleMute: () => void;
  seek: (timeSec: number) => void;
  playPrev: () => void;
  playNext: () => void;
  close: () => void;
  /** Backward-compat: старый register() — теперь noop, audio в провайдере. */
  register: (meta: VoicePlayerMeta, audio: HTMLAudioElement) => () => void;
}

const VoicePlayerContext = createContext<VoicePlayerCtx | null>(null);

export function VoicePlayerProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<VoicePlayerMeta | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playablesRef = useRef<Map<number, () => void>>(new Map());
  // Зеркалим meta в ref — onEnded listener мониться один раз ([] deps),
  // а нам надо получить актуальный messageId чтобы автоматически запустить следующее.
  const metaRef = useRef<VoicePlayerMeta | null>(null);

  // Listeners на единственный <audio>
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
      // Auto-play следующего: setTimeout(0) — даём React закоммитить
      // setPlaying(false) и audio выйти из 'ended' state до нового play()
      const currentId = metaRef.current?.messageId;
      if (currentId == null) {
        setMeta(null);
        return;
      }
      const ids = Array.from(playablesRef.current.keys()).sort((x, y) => x - y);
      const idx = ids.indexOf(currentId);
      if (idx < 0 || idx + 1 >= ids.length) {
        console.info("[voice] auto-next: no next available — closing bar", { currentId, ids });
        setMeta(null);
        setDuration(0);
        return;
      }
      const nextId = ids[idx + 1];
      const fn = playablesRef.current.get(nextId);
      console.info("[voice] auto-next →", nextId);
      if (fn) setTimeout(fn, 0);
    };
    const onTime = () => setCurrentTime(a.currentTime);
    const onLoaded = () => { if (isFinite(a.duration)) setDuration(a.duration); };
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("durationchange", onLoaded);
    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("durationchange", onLoaded);
    };
  }, []);

  // Зеркалим meta в ref для onEnded auto-next
  useEffect(() => { metaRef.current = meta; }, [meta]);

  // Гладкий currentTime через rAF (timeupdate ~4Hz, мало для waveform)
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => {
      const a = audioRef.current;
      if (a) setCurrentTime(a.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const play = useCallback((newMeta: VoicePlayerMeta, src: string) => {
    const a = audioRef.current;
    if (!a) return;
    // Сравниваем с attribute (то что мы сами установили), а не a.src (абсолютный URL).
    // Если src отличается — переустанавливаем + load() чтобы браузер сразу подгрузил.
    if (a.getAttribute("src") !== src) {
      a.src = src;
      try { a.load(); } catch { /* ignore */ }
      setCurrentTime(0);
      setDuration(newMeta.durationSec || 0);
    }
    a.playbackRate = playbackRate;
    a.muted = muted;
    setMeta(newMeta);
    void a.play().catch((err) => { console.warn("[voice] play() rejected:", err); });
  }, [playbackRate, muted]);

  // Backward-compat: старый VoiceMessage иногда зовёт register с готовым audio.
  // Теперь игнорируем переданный audio и работаем со своим внутренним.
  const register = useCallback((newMeta: VoicePlayerMeta, _audio: HTMLAudioElement) => {
    setMeta(newMeta);
    return () => { /* unmount cleanup — meta остаётся, audio тоже играет */ };
  }, []);

  const registerPlayable = useCallback((messageId: number, playFn: () => void) => {
    playablesRef.current.set(messageId, playFn);
    return () => {
      const m = playablesRef.current.get(messageId);
      if (m === playFn) playablesRef.current.delete(messageId);
    };
  }, []);

  const togglePlayPause = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      void a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, []);

  const cyclePlaybackRate = useCallback(() => {
    setPlaybackRate((r) => {
      const next = r === 1 ? 1.5 : r === 1.5 ? 2 : 1;
      const a = audioRef.current;
      if (a) a.playbackRate = next;
      return next;
    });
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      const a = audioRef.current;
      if (a) a.muted = next;
      return next;
    });
  }, []);

  const seek = useCallback((timeSec: number) => {
    const a = audioRef.current;
    if (!a) return;
    if (!isFinite(timeSec)) return;
    a.currentTime = Math.max(0, Math.min(a.duration || timeSec, timeSec));
    setCurrentTime(a.currentTime);
  }, []);

  const jumpBy = useCallback((dir: 1 | -1) => {
    const current = meta?.messageId;
    if (current == null) return;
    const ids = Array.from(playablesRef.current.keys()).sort((a, b) => a - b);
    const idx = ids.indexOf(current);
    if (idx < 0) return;
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= ids.length) return;
    const fn = playablesRef.current.get(ids[targetIdx]);
    if (fn) fn();
  }, [meta]);

  const playPrev = useCallback(() => jumpBy(-1), [jumpBy]);
  const playNext = useCallback(() => jumpBy(1), [jumpBy]);

  const close = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      try { a.pause(); a.currentTime = 0; a.removeAttribute("src"); a.load(); } catch { /* ignore */ }
    }
    setMeta(null);
    setPlaying(false);
    setCurrentTime(0);
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (a) a.playbackRate = playbackRate;
  }, [playbackRate]);

  return (
    <VoicePlayerContext.Provider
      value={{
        meta, playing, currentTime, duration, playbackRate, muted,
        play, register, registerPlayable,
        togglePlayPause, cyclePlaybackRate, toggleMute, seek,
        playPrev, playNext, close,
      }}
    >
      {children}
      {/* Единственный <audio> на всё приложение — переживает unmount VoiceMessage */}
      <audio ref={audioRef} hidden preload="auto"/>
    </VoicePlayerContext.Provider>
  );
}

export function useVoicePlayer(): VoicePlayerCtx {
  const ctx = useContext(VoicePlayerContext);
  if (!ctx) {
    return {
      meta: null, playing: false, currentTime: 0, duration: 0, playbackRate: 1, muted: false,
      play: () => {},
      register: () => () => {},
      registerPlayable: () => () => {},
      togglePlayPause: () => {},
      cyclePlaybackRate: () => {},
      toggleMute: () => {},
      seek: () => {},
      playPrev: () => {},
      playNext: () => {},
      close: () => {},
    };
  }
  return ctx;
}
